import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Truck, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { ProveedorFormModal } from './ProveedorFormModal';

type Row = {
  id: string;
  rfc: string;
  razon_social: string;
  nombre_comercial?: string;
  tipo_persona: 'fisica' | 'moral';
  tipo_proveedor: string;
  email?: string;
  telefono?: string;
  giro_comercial?: string;
  tamano_empresa?: string;
  inhabilitado?: boolean;
  promedio_evaluaciones?: number;
  num_evaluaciones?: number;
  cxp_pendientes?: number;
  cxp_saldo_total?: number;
  activo: boolean;
};

export function ProveedoresPage() {
  const [q, setQ] = useState('');
  const [tipo, setTipo] = useState('');
  const [open, setOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['proveedores', q, tipo],
    queryFn: () => {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (tipo) p.set('tipo', tipo);
      p.set('limit', '200');
      return api<{ total: number; items: Row[] }>(`/proveedores?${p.toString()}`);
    },
  });

  const items = data?.items ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-ink-mute">Recursos Materiales · Proveedores</p>
          <h1 className="mt-1 font-display text-[28px] font-bold tracking-tight text-ink">Padrón de proveedores</h1>
          <p className="mt-1 text-[14px] text-ink-mute">
            RFC, datos fiscales SAT, sanciones vigentes y evaluaciones de desempeño.
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus size={16} /> Nuevo proveedor
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_220px]">
        <Input
          name="q"
          placeholder="Buscar por razón social, RFC o nombre comercial…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          leading={<Search size={16} />}
        />
        <select
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="h-10 rounded-md border border-border bg-surface px-3 text-[14px] text-ink shadow-subtle"
        >
          <option value="">Todos los tipos</option>
          <option value="general">General</option>
          <option value="servicios">Servicios</option>
          <option value="materiales">Materiales</option>
          <option value="construccion">Construcción</option>
          <option value="tecnologia">Tecnología</option>
          <option value="consultoria">Consultoría</option>
          <option value="otro">Otro</option>
        </select>
      </div>

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-10 text-center text-[13px] text-ink-mute">Cargando…</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-10 text-center">
              <Truck className="h-7 w-7 text-ink-soft" />
              <p className="text-[13px] text-ink-mute">
                {q || tipo ? 'Sin coincidencias para el filtro.' : 'Aún no hay proveedores. Crea el primero.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">RFC</th>
                    <th className="px-5 py-3">Razón social</th>
                    <th className="px-5 py-3">Tipo</th>
                    <th className="px-5 py-3">Giro</th>
                    <th className="px-5 py-3 text-center">Eval.</th>
                    <th className="px-5 py-3 text-right">CxP saldo</th>
                    <th className="px-5 py-3">Estatus</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-muted/50">
                      <td className="num px-5 py-3 font-semibold text-brand-700 dark:text-brand-300">{r.rfc}</td>
                      <td className="px-5 py-3">
                        <div className="text-ink">{r.razon_social}</div>
                        {r.nombre_comercial && (
                          <div className="text-[11px] text-ink-mute">{r.nombre_comercial}</div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-mute">
                          {r.tipo_proveedor}
                        </span>
                      </td>
                      <td className="px-5 py-3 max-w-[180px] truncate text-ink-mute">{r.giro_comercial ?? '—'}</td>
                      <td className="px-5 py-3 text-center">
                        {r.num_evaluaciones && r.num_evaluaciones > 0 ? (
                          <span className="num rounded-full bg-emerald-50 px-2 py-0.5 text-[12px] font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                            {r.promedio_evaluaciones?.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-[12px] text-ink-soft">—</span>
                        )}
                      </td>
                      <td className="num px-5 py-3 text-right">
                        {r.cxp_saldo_total && r.cxp_saldo_total > 0 ? (
                          <span className="font-semibold">{fmt.money(r.cxp_saldo_total)}</span>
                        ) : (
                          <span className="text-ink-soft">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {r.inhabilitado ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">
                            <ShieldAlert size={11} /> Inhabilitado
                          </span>
                        ) : r.activo ? (
                          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                            Activo
                          </span>
                        ) : (
                          <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-medium text-ink-mute dark:bg-white/5">
                            Inactivo
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Link to={`/app/proveedores/${r.id}`}>
                          <Button variant="ghost" size="sm">Ver</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className={cn(
                'flex items-center justify-between px-5 py-3 border-t border-border text-[12px] text-ink-mute'
              )}>
                <span>{items.length} de {data?.total ?? 0} proveedores</span>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <ProveedorFormModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={() => { setOpen(false); refetch(); }}
      />
    </div>
  );
}
